import { navIcon, navIconAccent } from "@/lib/nav-icons";
import type { NavSection } from "@/lib/routes";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

const sizeMap = {
	sm: "size-9",
	md: "size-11",
	lg: "size-12",
} as const;

const iconSizeMap = {
	sm: "size-4",
	md: "size-5",
	lg: "size-6",
} as const;

/**
 * Premium MORE / nav icon badge with electric green glow + micro-hover.
 */
export function NavIconBadge({
	icon,
	active,
	size = "md",
	className,
	Icon: IconOverride,
}: {
	icon: NavSection["icon"];
	active?: boolean;
	size?: "sm" | "md" | "lg";
	className?: string;
	Icon?: LucideIcon;
}) {
	const Icon = IconOverride ?? navIcon(icon);
	const accent = navIconAccent(icon);

	return (
		<span
			className={cn(
				"nav-icon-badge",
				sizeMap[size],
				active && "nav-icon-badge-active",
				className,
			)}
			style={{
				// Per-route tint of the green family (not rainbow)
				boxShadow: active
					? `0 0 20px color-mix(in srgb, ${accent} 45%, transparent)`
					: undefined,
				borderColor: `color-mix(in srgb, ${accent} 35%, transparent)`,
			}}
		>
			<span
				className="absolute inset-0 rounded-[14px] opacity-40"
				style={{
					background: `radial-gradient(circle at 30% 30%, ${accent}55, transparent 65%)`,
				}}
				aria-hidden
			/>
			<Icon
				className={cn(iconSizeMap[size], "relative z-[1]")}
				style={{ color: accent }}
				strokeWidth={1.75}
				aria-hidden
			/>
		</span>
	);
}
